import React, { useState, useEffect } from 'react';
import { projectAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Unplug, Link2, CheckCircle, AlertCircle, ExternalLink, 
  RefreshCw, Settings, X, Check, Key, Mail, Globe
} from 'lucide-react';
import { toast } from 'sonner';

export default function JiraPanel({ projectId, isOwner }) {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [jiraForm, setJiraForm] = useState({
    jira_url: '',
    jira_email: '',
    jira_token: '',
    project_key: ''
  });

  useEffect(() => {
    loadIntegrations();
  }, [projectId]);

  const loadIntegrations = async () => {
    try {
      const res = await projectAPI.getIntegrations(projectId);
      setIntegrations(res.data || {});
    } catch (err) {
      console.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!jiraForm.jira_url.trim() || !jiraForm.jira_email.trim() || !jiraForm.jira_token.trim() || !jiraForm.project_key.trim()) {
      toast.error('All fields are required');
      return;
    }
    setConnecting(true);
    try {
      await projectAPI.connectJira(projectId, jiraForm);
      setJiraForm({ jira_url: '', jira_email: '', jira_token: '', project_key: '' });
      setShowConnectForm(false);
      loadIntegrations();
      toast.success('Jira connected successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect Jira');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await projectAPI.disconnectJira(projectId);
      loadIntegrations();
      toast.success('Jira disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const jira = integrations.jira;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin h-7 w-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--surface-border)] flex items-center gap-2">
        <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">
          <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.005 1.005 0 0 0 23.013 0z"/>
          </svg>
          Jira Integration
        </Badge>
        
        {jira?.status === 'connected' && (
          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
            <CheckCircle className="w-4 h-4 mr-1" /> Connected
          </Badge>
        )}
        
        <div className="flex-1" />
        
        {isOwner && !jira && (
          <Button size="sm" className="h-8 text-xs bg-blue-500 hover:bg-blue-600 text-white" onClick={() => setShowConnectForm(true)} data-testid="connect-jira-btn">
            <Link2 className="w-4 h-4 mr-1" /> Connect Jira
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 max-w-xl">
          {/* Connected State */}
          {jira?.status === 'connected' && (
            <div className="space-y-4">
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4" data-testid="jira-connected">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-400">Jira Connected</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 opacity-40" />
                    <span className="text-xs opacity-60">Instance:</span>
                    <a href={jira.jira_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline flex items-center gap-1">
                      {jira.jira_url} <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 opacity-40" />
                    <span className="text-xs opacity-60">Account:</span>
                    <span className="text-xs">{jira.jira_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4 opacity-40" />
                    <span className="text-xs opacity-60">Project Key:</span>
                    <Badge variant="outline" className="text-xs">{jira.project_key}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 opacity-40" />
                    <span className="text-xs opacity-60">Connected:</span>
                    <span className="text-xs">{new Date(jira.connected_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              
              {/* Sync Info */}
              <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg p-4">
                <p className="text-xs font-medium mb-2">Sync Features</p>
                <ul className="space-y-1.5 text-xs opacity-60">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Tasks sync to Jira issues
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Bug reports sync to Jira bugs
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Epics sync to Jira epics
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Status changes sync both ways
                  </li>
                </ul>
              </div>
              
              {isOwner && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" 
                  onClick={handleDisconnect}
                  data-testid="disconnect-jira-btn"
                >
                  <Unplug className="w-4 h-4 mr-1" /> Disconnect Jira
                </Button>
              )}
            </div>
          )}

          {/* Not Connected State */}
          {!jira && !showConnectForm && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.005 1.005 0 0 0 23.013 0z"/>
                </svg>
              </div>
              <p className="text-sm font-medium mb-1">Connect to Jira</p>
              <p className="text-xs opacity-50 mb-4 max-w-xs mx-auto">
                Sync your tasks, bugs, and epics with your Jira project for seamless project management.
              </p>
              {isOwner ? (
                <Button className="bg-blue-500 hover:bg-blue-600 text-white text-xs" onClick={() => setShowConnectForm(true)} data-testid="start-connect-jira">
                  <Link2 className="w-4 h-4 mr-1" /> Connect Jira
                </Button>
              ) : (
                <p className="text-xs opacity-40">Only the project owner can connect integrations</p>
              )}
            </div>
          )}

          {/* Connect Form */}
          {showConnectForm && (
            <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Connect Jira</p>
                <Button variant="ghost" size="icon" className="h-7 w-6" onClick={() => setShowConnectForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs mb-1 block opacity-60">Jira Instance URL</Label>
                  <div className="relative">
                    <Globe className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-40" />
                    <Input 
                      value={jiraForm.jira_url} 
                      onChange={(e) => setJiraForm(f => ({ ...f, jira_url: e.target.value }))} 
                      placeholder="https://your-company.atlassian.net" 
                      className="h-9 text-sm bg-transparent border-[var(--surface-border)] pl-9" 
                      data-testid="jira-url-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs mb-1 block opacity-60">Email Address</Label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-40" />
                    <Input 
                      value={jiraForm.jira_email} 
                      onChange={(e) => setJiraForm(f => ({ ...f, jira_email: e.target.value }))} 
                      placeholder="you@company.com" 
                      className="h-9 text-sm bg-transparent border-[var(--surface-border)] pl-9" 
                      data-testid="jira-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs mb-1 block opacity-60">API Token</Label>
                  <div className="relative">
                    <Key className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-40" />
                    <Input 
                      type="password"
                      value={jiraForm.jira_token} 
                      onChange={(e) => setJiraForm(f => ({ ...f, jira_token: e.target.value }))} 
                      placeholder="Your Jira API token" 
                      className="h-9 text-sm bg-transparent border-[var(--surface-border)] pl-9" 
                      data-testid="jira-token-input"
                    />
                  </div>
                  <p className="text-xs opacity-40 mt-1">
                    Generate an API token at{' '}
                    <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      Atlassian Account Settings
                    </a>
                  </p>
                </div>
                
                <div>
                  <Label className="text-xs mb-1 block opacity-60">Project Key</Label>
                  <div className="relative">
                    <Settings className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-40" />
                    <Input 
                      value={jiraForm.project_key} 
                      onChange={(e) => setJiraForm(f => ({ ...f, project_key: e.target.value.toUpperCase() }))} 
                      placeholder="e.g., PROJ" 
                      className="h-9 text-sm bg-transparent border-[var(--surface-border)] pl-9 uppercase" 
                      data-testid="jira-project-key-input"
                    />
                  </div>
                  <p className="text-xs opacity-40 mt-1">The short identifier for your Jira project</p>
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  className="bg-blue-500 hover:bg-blue-600 text-white text-xs flex-1" 
                  onClick={handleConnect}
                  disabled={connecting}
                  data-testid="submit-jira-connect"
                >
                  {connecting ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-1" />
                  )}
                  {connecting ? 'Connecting...' : 'Connect'}
                </Button>
                <Button variant="ghost" className="text-xs" onClick={() => setShowConnectForm(false)} data-testid="cancel-jira-connect">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg p-4">
            <p className="text-xs font-medium mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              How to get your API Token
            </p>
            <ol className="space-y-1 text-xs opacity-60 list-decimal list-inside">
              <li>Go to Atlassian Account Settings → Security</li>
              <li>Click "Create and manage API tokens"</li>
              <li>Create a new token with a descriptive label</li>
              <li>Copy the token and paste it above</li>
            </ol>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
